/**
 * Inline tag editor for one or many repositories (admin).
 *
 * One value picker per unarchived dimension, with inline value creation.
 * Single-repo mode edits the full tag map (PUT semantics). Bulk mode applies
 * only the dimensions the admin explicitly chose, merged per repo over its
 * existing tags — so bulk-tagging a department never wipes anyone's LOB.
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Spinner } from '@systemdox/ui';
import { projectsApi, type TaxonomyDimension } from '@/services/projectsApi';
import { cn } from '@/lib/utils';

const UNSET = '';

export function RepoTagEditor({
  repoLabel,
  dimensions,
  initial,
  bulk = false,
  onApply,
  onCancel,
  onValueCreated,
}: {
  /** "acme/api" or "12 selected repositories". */
  repoLabel: string;
  dimensions: TaxonomyDimension[];
  /** Current tags (single-repo mode); ignored for bulk. */
  initial: Record<string, string>;
  bulk?: boolean;
  /** Single: the full new tag map. Bulk: only the chosen dimensions. */
  onApply: (chosen: Record<string, string>) => Promise<void>;
  onCancel: () => void;
  onValueCreated: () => Promise<void>;
}) {
  const [chosen, setChosen] = useState<Record<string, string>>(bulk ? {} : { ...initial });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [newValue, setNewValue] = useState('');

  const active = dimensions.filter((d) => !d.archivedAt);

  async function createValue(dimKey: string) {
    const label = newValue.trim();
    if (!label) return;
    const valueSlug = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    if (!valueSlug) return;
    setError(null);
    try {
      await projectsApi.createValue(dimKey, { valueSlug, label });
      await onValueCreated();
      setChosen((prev) => ({ ...prev, [dimKey]: valueSlug }));
      setAddingTo(null);
      setNewValue('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create value');
    }
  }

  async function apply() {
    setSaving(true);
    setError(null);
    try {
      await onApply(chosen);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save tags');
      setSaving(false);
      return;
    }
    setSaving(false);
  }

  return (
    <div className="mt-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-3">
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
        {bulk ? `Tag ${repoLabel} — only the dimensions you set are applied` : `Tags for ${repoLabel}`}
      </p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {active.map((dim) => (
          <label key={dim.dimKey} className="text-xs text-slate-600 dark:text-slate-400">
            {dim.label}
            <div className="mt-0.5 flex items-center gap-1">
              <select
                value={chosen[dim.dimKey] ?? UNSET}
                onChange={(e) =>
                  setChosen((prev) => {
                    const next = { ...prev };
                    if (e.target.value === UNSET) delete next[dim.dimKey];
                    else next[dim.dimKey] = e.target.value;
                    return next;
                  })
                }
                className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm text-slate-900 dark:text-slate-100"
              >
                <option value={UNSET}>{bulk ? '(leave unchanged)' : '(none)'}</option>
                {dim.values
                  .filter((v) => !v.archivedAt)
                  .map((v) => (
                    <option key={v.valueSlug} value={v.valueSlug}>
                      {v.label}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                title={`New ${dim.label} value`}
                onClick={() => {
                  setAddingTo(addingTo === dim.dimKey ? null : dim.dimKey);
                  setNewValue('');
                }}
                className="p-1.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
            {addingTo === dim.dimKey && (
              <div className="mt-1 flex items-center gap-1">
                <input
                  autoFocus
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && createValue(dim.dimKey)}
                  placeholder={`New ${dim.label.toLowerCase()}…`}
                  className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-sm"
                />
                <button
                  type="button"
                  onClick={() => createValue(dim.dimKey)}
                  className="px-2 py-1 rounded text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100"
                >
                  Add
                </button>
              </div>
            )}
          </label>
        ))}
      </div>
      {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={apply}
          disabled={saving || (bulk && Object.keys(chosen).length === 0)}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium',
            'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50',
          )}
        >
          {saving && <Spinner size="sm" tone="current" />}
          Apply
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1 rounded text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
